import { FunctionComponent } from "react";

import { Box, Button, Image, Link, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, VStack } from "@chakra-ui/react";
import sanitizeHtml from "sanitize-html";

import currentLocationImage from "../../assets/images/undraw_current-location_c8qn.svg";

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export interface GlobalEngagePopupBannerProps {
    title: string;
    infoLinkText: string;
    description?: string;
    note?: string;
    engageLinkText?: string;
    engageLink?: string;
    infoLink?: string;
    supportingButtonText?: string;
    supportingButtonLink?: string;
}
const ALLOWED_TAGS = ["a", "b", "br", "em", "i", "li", "ol", "p", "span", "strong", "u", "ul"];
const ALLOWED_ATTRIBUTES = {
    a: ["href", "target", "rel"],
};

const sanitizeBannerHtml = (html?: string) =>
    sanitizeHtml(html ?? "", {
        allowedTags: ALLOWED_TAGS,
        allowedAttributes: ALLOWED_ATTRIBUTES,
    });

export const GlobalEngagePopupModal: FunctionComponent<Props & GlobalEngagePopupBannerProps> = ({
    isOpen,
    onClose,
    ...bannerProps
}: Props & GlobalEngagePopupBannerProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent borderRadius="10px" overflow="hidden" boxShadow="2xl">
                <ModalHeader px={8} py={6} fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                    {bannerProps.title}
                </ModalHeader>
                <ModalCloseButton top={6} right={6} fontSize="18px" color="gray.700" />

                <ModalBody p={0}>
                    <Box bg="#E8D7B2" px={10} py={5}>
                        <Text
                            color="gray.900"
                            fontSize={{ base: "md", md: "lg" }}
                            lineHeight="1.35"
                            fontWeight="medium"
                            sx={{
                                a: { color: "#0077B6", fontWeight: "bold", textDecoration: "underline" },
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizeBannerHtml(bannerProps.description) }}
                        />
                    </Box>

                    <VStack spacing={8} px={10} py={8} align="stretch">
                        <Box
                            alignSelf="center"
                            width={{ base: "180px", md: "240px" }}
                            height={{ base: "120px", md: "170px" }}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Image src={currentLocationImage} alt="Current location illustration" maxW="100%" maxH="100%" objectFit="contain" />
                        </Box>

                        <Text color="gray.900" lineHeight="1.35" textAlign="center" fontSize={{ base: "md", md: "lg" }}>
                            {bannerProps.note} <br />
                            <Link href={bannerProps.engageLink} color="#0077B6" textDecor="underline" fontWeight="semibold" isExternal>
                                {bannerProps.engageLinkText}
                            </Link>
                        </Text>

                        <Box display="flex" justifyContent="space-between" alignItems="center" pb={2} gap="1.5rem">
                            <Box>
                                {bannerProps.supportingButtonText && bannerProps.supportingButtonLink && (
                                    <Button
                                        as="a"
                                        href={bannerProps.supportingButtonLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="md"
                                        variant="outline"
                                        fontSize="md"
                                        fontWeight="bold"
                                        _hover={{ bg: "#65686931" }}
                                    >
                                        {bannerProps.supportingButtonText}
                                    </Button>
                                )}
                            </Box>
                            <Button
                                onClick={onClose}
                                size="md"
                                bg="#2D6A94"
                                color="white"
                                px={8}
                                borderRadius="10px"
                                fontSize="md"
                                fontWeight="bold"
                                _hover={{ bg: "#255A7C" }}
                            >
                                Got it
                            </Button>
                        </Box>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
